import React, { useEffect, useRef, useState } from "react";

// "1,234.5" -> 1234.5, "(12)" -> -12, "" -> null; undefined if it isn't a number
const parseInput = (text) => {
  let cleaned = text.trim().replace(/,/g, "");
  if (cleaned === "") return null;
  const bracketed = /^\((.*)\)$/.exec(cleaned);
  if (bracketed) cleaned = `-${bracketed[1]}`;
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(cleaned)) return undefined;
  return Number(cleaned);
};

// A spreadsheet-style cell. Shows `display` until edited; editing shows the raw
// number between the format's fixed prefix and suffix.
//   Click to select, arrows move, Enter/F2/double-click or typing edits,
//   Enter commits and moves down, Tab commits and moves across, Esc cancels,
//   Delete clears.
const GridCell = ({
  display,
  raw,
  prefix = "",
  suffix = "",
  selected,
  editable = true,
  clearable = true,
  className = "",
  onSelect,
  onCommit,
  onMove,
}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [invalid, setInvalid] = useState(false);
  const cellRef = useRef(null);
  const inputRef = useRef(null);
  // Set when a key has already ended the edit, so the input's blur is ignored
  const handledByKey = useRef(false);

  useEffect(() => {
    if (selected && !editing) cellRef.current?.focus();
  }, [selected, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEdit = (initial) => {
    if (!editable) return;
    setText(initial ?? (raw === null || raw === undefined ? "" : String(raw)));
    setInvalid(false);
    handledByKey.current = false;
    setEditing(true);
  };

  const stopEdit = () => {
    setEditing(false);
    setInvalid(false);
  };

  // Returns false if the text isn't a number (and stays in edit mode)
  const commit = () => {
    const value = parseInput(text);
    if (value === undefined || (value === null && !clearable)) {
      setInvalid(true);
      return false;
    }
    stopEdit();
    if (value !== (raw ?? null)) onCommit(value);
    return true;
  };

  const handleCellKeyDown = (e) => {
    const moves = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    if (moves[e.key]) {
      e.preventDefault();
      onMove(...moves[e.key]);
    } else if (e.key === "Tab") {
      e.preventDefault();
      onMove(0, e.shiftKey ? -1 : 1);
    } else if (e.key === "Enter" || e.key === "F2") {
      e.preventDefault();
      startEdit();
    } else if ((e.key === "Delete" || e.key === "Backspace") && editable && clearable) {
      e.preventDefault();
      if ((raw ?? null) !== null) onCommit(null);
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      startEdit(e.key);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (commit()) {
        handledByKey.current = true;
        if (e.key === "Enter") onMove(e.shiftKey ? -1 : 1, 0);
        else onMove(0, e.shiftKey ? -1 : 1);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      handledByKey.current = true;
      stopEdit();
    }
  };

  // Clicking away commits, or throws away text that isn't a number
  const handleBlur = () => {
    if (handledByKey.current) return;
    if (!commit()) stopEdit();
  };

  const classes = [
    "grid-cell",
    className,
    selected ? "selected" : "",
    editing ? "editing" : "",
    invalid ? "invalid" : "",
    editable ? "" : "readonly",
  ].join(" ");

  return (
    <td className={classes}>
      {editing ? (
        <div className="grid-cell-edit">
          {prefix && <span className="grid-cell-affix">{prefix}</span>}
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setInvalid(false);
            }}
            onKeyDown={handleInputKeyDown}
            onBlur={handleBlur}
          />
          {suffix && <span className="grid-cell-affix">{suffix}</span>}
        </div>
      ) : (
        <div
          ref={cellRef}
          className="grid-cell-display"
          tabIndex={0}
          onClick={onSelect}
          onFocus={() => !selected && onSelect()}
          onDoubleClick={() => startEdit()}
          onKeyDown={handleCellKeyDown}
        >
          {display}
        </div>
      )}
    </td>
  );
};

export default GridCell;
